import { useQuery } from "@tanstack/react-query";

import { fetchErrorReport, fetchEnhancingListings } from "api/error-report-api";

import { toCurrency } from "shared/helpers";

export interface IErrorReportData {
  /* eslint-disable @typescript-eslint/naming-convention */
  brand?: string;
  color?: string | null;
  date_created: string;
  error?: string;
  name?: string;
  product_id?: string | number;
  quantity: number;
  source: string;
  status: "PENDING" | "ERROR" | "IGNORED";
  total_wholesale_cost?: number;
  wholesale_cost?: number | string;
  /* eslint-enable @typescript-eslint/naming-convention */
}

interface IPaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

interface IUseErrorReportParams {
  retailerId: string;
  page?: number;
  limit?: number;
}

export const useErrorReport = ({ retailerId, page = 1, limit = 20 }: IUseErrorReportParams) => {
  const offset = (page - 1) * limit;

  const { data: errorResponse, ...errorRest } = useQuery<IPaginatedResponse<IErrorReportData> | null>({
    queryKey: ["error-report", retailerId, page, limit],
    queryFn: () => fetchErrorReport({ retailerId, offset, limit }),
    enabled: Boolean(retailerId)
  });

  const { data: enhancedResponse, ...enhancedRest } = useQuery<IPaginatedResponse<IErrorReportData> | null>({
    queryKey: ["enhancing-listings", retailerId, page, limit],
    queryFn: () => fetchEnhancingListings({ retailerId, offset, limit }),
    enabled: Boolean(retailerId)
  });

  // Extraer arrays de results
  const errorData = errorResponse?.results || [];
  const enhancedData = enhancedResponse?.results || [];

  if (!errorResponse || !enhancedResponse) {
    return {
      data: { error: [], pending: [] },
      pagination: {
        error: {
          totalCount: 0,
          currentPage: page,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        },
        pending: {
          totalCount: 0,
          currentPage: page,
          totalPages: 0,
          hasNext: false,
          hasPrev: false
        }
      },
      isLoading: true
    };
  }

  const error: IErrorReportData[] = [];
  const pending: IErrorReportData[] = [];
  const ERROR_CODE_REGEX = /\(\d+\)\s*/g;

  for (const row of errorData) {
    const total = toCurrency(row.total_wholesale_cost);
    const value = {
      ...row,
      originalError: row.error,
      error: typeof row.error === "string"
        ? row.error.replace(ERROR_CODE_REGEX, "").replace(/\s+/g, " ").trim()
        : "",
      total,
      date: row.date_created,
      status: row.status
    };
    error.push(value);
  }

  for (const row of enhancedData) {
    const total = toCurrency(row.total_wholesale_cost);
    const value = {
      ...row,
      total,
      date: row.date_created,
      status: row.status,
    };
    pending.push(value);
  }

  const errorTotalPages = Math.ceil((errorResponse.count || 0) / limit);
  const pendingTotalPages = Math.ceil((enhancedResponse.count || 0) / limit);

  return {
    data: { error, pending },
    pagination: {
      error: {
        totalCount: errorResponse.count || 0,
        currentPage: page,
        totalPages: errorTotalPages,
        hasNext: Boolean(errorResponse.next),
        hasPrev: Boolean(errorResponse.previous)
      },
      pending: {
        totalCount: enhancedResponse.count || 0,
        currentPage: page,
        totalPages: pendingTotalPages,
        hasNext: Boolean(enhancedResponse.next),
        hasPrev: Boolean(enhancedResponse.previous)
      }
    },
    ...errorRest,
    ...enhancedRest,
    isLoading: errorRest.isLoading || enhancedRest.isLoading,
    refetch: async () => {
      await errorRest.refetch();
      await enhancedRest.refetch();
    },
  };
};
